import { useState, useCallback } from "react";
import type { SortIndicatorProps } from "../SortIndicator";

export interface UseTableSortOptions<K extends string> {
  defaultColumn: K;
  defaultDirection?: "asc" | "desc";
}

export interface UseTableSortResult<K extends string> {
  sortColumn: K;
  sortDirection: "asc" | "desc";
  toggleSort: (column: K) => void;
  getSortIndicatorProps: (column: K) => Pick<SortIndicatorProps, "direction" | "active">;
}

export function useTableSort<K extends string>(
  options: UseTableSortOptions<K>,
): UseTableSortResult<K> {
  const [sortColumn, setSortColumn] = useState<K>(options.defaultColumn);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    options.defaultDirection ?? "desc",
  );

  const toggleSort = useCallback(
    (column: K) => {
      if (column === sortColumn) {
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortColumn(column);
        setSortDirection(options.defaultDirection ?? "desc");
      }
    },
    [sortColumn, options.defaultDirection],
  );

  const getSortIndicatorProps = useCallback(
    (column: K): Pick<SortIndicatorProps, "direction" | "active"> => ({
      active: column === sortColumn,
      direction: column === sortColumn ? sortDirection : "none",
    }),
    [sortColumn, sortDirection],
  );

  return { sortColumn, sortDirection, toggleSort, getSortIndicatorProps };
}
